import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { useAuthStore } from '@/lib/stores/auth';
import { api } from '@/lib/api';
import {
  useNotificationWebSocket,
  type NotificationPayload,
} from '@/hooks/useNotificationWebSocket';

export interface Notification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  metadata: Record<string, unknown>;
  isRead: boolean;
  createdAt: string;
}

interface NotificationContextValue {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  isPanelOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  loadMore: () => Promise<void>;
  hasMore: boolean;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

const PAGE_SIZE = 20;

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { token, user } = useAuthStore();
  const isAuthenticated = !!token && !!user;
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const data = await api.get<{ unreadCount: number }>('/api/notifications/unread-count');
      setUnreadCount(data.unreadCount);
    } catch {
      // Silent fail
    }
  }, [token]);

  // Fetch notifications list
  const fetchNotifications = useCallback(
    async (pageNum: number, append = false) => {
      if (!token) return;
      setIsLoading(true);
      try {
        const data = await api.get<{ data: Notification[]; total: number }>(`/api/notifications?page=${pageNum}&limit=${PAGE_SIZE}`);
        if (append) {
          setNotifications((prev) => [...prev, ...data.data]);
        } else {
          setNotifications(data.data);
        }
        setHasMore(pageNum * PAGE_SIZE < data.total);
      } catch {
        // Silent fail
      } finally {
        setIsLoading(false);
      }
    },
    [token],
  );

  // Initial load
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchUnreadCount();
      fetchNotifications(1);
    } else {
      setNotifications([]);
      setUnreadCount(0);
      setPage(1);
    }
  }, [isAuthenticated, token, fetchUnreadCount, fetchNotifications]);

  // Handle real-time notification
  const handleNewNotification = useCallback((payload: NotificationPayload) => {
    const newNotif: Notification = {
      id: payload.id,
      type: payload.type,
      title: payload.title,
      body: payload.body,
      metadata: payload.metadata,
      isRead: false,
      createdAt: payload.createdAt,
    };
    setNotifications((prev) => [newNotif, ...prev]);
    setUnreadCount((prev) => prev + 1);
  }, []);

  useNotificationWebSocket({
    token,
    enabled: isAuthenticated,
    onNotification: handleNewNotification,
  });

  const openPanel = useCallback(() => setIsPanelOpen(true), []);
  const closePanel = useCallback(() => setIsPanelOpen(false), []);
  const togglePanel = useCallback(() => setIsPanelOpen((v) => !v), []);

  const markAsRead = useCallback(
    async (id: string) => {
      if (!token) return;
      try {
        await api.patch(`/api/notifications/${id}/read`);
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch {
        // Silent fail
      }
    },
    [token],
  );

  const markAllAsRead = useCallback(async () => {
    if (!token) return;
    try {
      await api.patch('/api/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      // Silent fail
    }
  }, [token]);

  const loadMore = useCallback(async () => {
    const nextPage = page + 1;
    setPage(nextPage);
    await fetchNotifications(nextPage, true);
  }, [page, fetchNotifications]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        isLoading,
        isPanelOpen,
        openPanel,
        closePanel,
        togglePanel,
        markAsRead,
        markAllAsRead,
        loadMore,
        hasMore,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return ctx;
}
